<template>
  <div class="d-flex w-100">
    <div class="w-50">
      <div class="spnod-admin__account--section">
        <h5 class="spnod-admin__account--header">Personal Details</h5>
        <b-row>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">First Name:</span> {{ user.firstName }}</b-col>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Last Name:</span> {{ user.lastName }}</b-col>
        </b-row>
        <b-row>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Phone:</span> {{ user.phone }}</b-col>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Email:</span> {{ user.email }}</b-col>
        </b-row>
        <b-row v-if="isPriveledgedUser">
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">DOB:</span> {{ dob }}</b-col>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Notification Method:</span> {{ user.notificationMethod }}</b-col>
        </b-row>
      </div>
      <div v-if="isPriveledgedUser" class="spnod-admin__account--section">
        <h5 class="spnod-admin__account--header">Address</h5>
        <b-row>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Street: </span>{{ street }}</b-col>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Street Line 2: </span>{{ street2 }}</b-col>
        </b-row>
        <b-row>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">City: </span>{{ city }}</b-col>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">State: </span>{{ state }}</b-col>
        </b-row>
        <b-row>
          <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Zip: </span>{{ zipcode }}</b-col>
        </b-row>
      </div>
      <div class="spnod-admin__account--section">
        <h5 class="spnod-admin__account--header">Groups</h5>
        <ul>
          <li class="spnod-admin__form--list" v-for="group in user.groups">{{ group.name }}</li>
        </ul>
      </div>
      <div v-if="isPriveledgedUser" class="spnod-admin__account--section">
        <h5 class="spnod-admin__account--header">Parent(s) / Guardian(s)</h5>
        <ul v-if="displayParents">
          <li class="spnod-admin__form--list" v-for="parent in user.parents">{{ parent.name }}</li>
        </ul>
      </div>
      <div class="spnod-admin__account--section">
        <h5 class="spnod-admin__account--header">Subscriptions</h5>
        <div v-if="loadingSubscriptions">Loading...</div>
        <div v-else>
          <b-row v-if="isPriveledgedUser">
            <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Plan: </span>{{ nickname }}</b-col>
            <b-col cols="12" xl="6" class="pb-2"><span class="text-muted">Status: </span>{{ active }}</b-col>
          </b-row>
          <b-row v-if="sub && isPriveledgedUser">
            <b-col v-if="!sub.cancel_at_period_end" cols="12" xl="6" class="pb-2"><span class="text-muted">Due on: </span>{{ showTime(sub.current_period_end) }}</b-col>
            <b-col v-else cols="12" xl="6" class="pb-2"><span class="text-muted">Ends on: </span>{{ showTime(sub.cancel_at) }}</b-col>
          </b-row>
          <b-row>
            <b-col>
              <p v-if="forClient && !sub" @click="handleSubscribe" class="text-primary cursor-pointer">Subscribe this user</p>
              <div v-else>
                <p v-if="displayDisclaimer" class="text-danger pb-2">Removing this user from their group will cancel their individual subscription!</p>
                <router-link to="/subscriptions">Manage Subscriptions</router-link>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <div v-if="isPriveledgedUser" class="spnod-admin__account--section">
        <h5 class="spnod-admin__account--header">Nutritionist</h5>
        <b-row>
          <b-col class="pb-2"><span class="text-muted">Current Nutritionist: </span>{{ nutritionist }}</b-col>
        </b-row>
      </div>
      <div v-if="isPriveledgedUser" class="spnod-admin__account--section">
        <h5 class="spnod-admin__account--header">Dietary Restrictions</h5>
        <b-row>
          <b-col class="pb-2">{{ user.dietaryRequirements }}</b-col>
        </b-row>
      </div>
      <b-button class="spnod-admin__form--button" @click="$emit('toggleEdit')" size="sm">Edit</b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'UserAccount',
  props: ['user', 'plan', 'sub', 'isSending', 'loadingMessages', 'isAdmin', 'loadingSubscriptions', 'forClient'],
  computed: {
    ...mapGetters(['getMyInfo', 'groups']),
    displayParents() {
      return this.user.parents && this.user.parents.length > 0
    },
    nickname() {
      if (this.plan && this.plan.nickname) {
        return this.plan.nickname
      } else if (this.plan) {
        return 'Group Plan'
      } else {
        return ''
      }
    },
    active() {
      return this.sub ? 'Active' : 'Inactive'
    },
    nutritionist() {
      return (this.user.nutritionist && this.user.nutritionist.employee) ? this.user.nutritionist.employee.name : ''
    },
    street() {
      return (this.user.address) ? this.user.address.address_line1 : ''
    },
    street2() {
      return (this.user.address) ? this.user.address.address_line2 : ''
    },
    city() {
      return (this.user.address) ? this.user.address.city : ''
    },
    state() {
      return (this.user.address) ? this.user.address.state : ''
    },
    zipcode() {
      return (this.user.address) ? this.user.address.zip : ''
    },
    myAccount() {
      if (this.getMyInfo) return this.user._id === this.getMyInfo._id
    },
    displayEdit() {
      if (this.getMyInfo) return this.myAccount || this.getMyInfo.hasRoles
    },
    dob() {
      // Remove trailing Z from UTC & format date
      if (!this.user.DOB) return
      const utc = this.user.DOB
      const time = utc.substring(0, utc.length - 1)
      return moment(time).format('MM/DD/YYYY')
    },
    isPriveledgedUser() {
      return this.isAdmin || !this.forClient
    },
    displayDisclaimer() {
      return this.forClient && this.plan.nickname === 'Group Plan'
    }
  },
  methods: {
    ...mapActions(['cancelSubscription', 'getGroups', 'setNewUser']),
    showTime(value) { return moment.unix(value).format('MM/DD/YYYY') },
    handleSubscribe() {
      this.setNewUser(this.user)
      this.$router.push('/user/payment')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/app/components/UserAccount/UserAccount.vue?50ccd6e3